import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertCircle, RotateCcw, Eye } from 'lucide-react';

interface ShapeItem {
  id: string;
  emoji: string;
  name: string;
  description: string;
  properties: string[];
}

interface ShapeRecognitionComponentProps {
  resource: {
    type: string;
    name: string;
    shapes: ShapeItem[];
    instructions: string;
    showHints: boolean;
    allowCorrections: boolean;
    difficultyLevel: 'easy' | 'medium' | 'hard';
  };
  onAction?: (action: string, data?: any) => void;
}

const ShapeRecognitionComponent: React.FC<ShapeRecognitionComponentProps> = ({ 
  resource, 
  onAction 
}) => {
  const [currentShapeIndex, setCurrentShapeIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'correct' | 'incorrect' | 'hint'; message: string } | null>(null);
  const [completedShapes, setCompletedShapes] = useState<Set<number>>(new Set());
  const [showHints] = useState(resource.showHints);
  const [attempts, setAttempts] = useState(0);

  const currentShape = resource.shapes[currentShapeIndex];
  const isCompleted = completedShapes.has(currentShapeIndex);
  const isLastShape = currentShapeIndex === resource.shapes.length - 1;

  // Reset component when resource changes
  useEffect(() => {
    setCurrentShapeIndex(0);
    setShowAnswer(false);
    setUserAnswer('');
    setFeedback(null);
    setCompletedShapes(new Set());
    setAttempts(0);
  }, [resource]);

  const handleAnswerSubmit = useCallback(() => {
    if (!userAnswer.trim()) return;

    const normalizedAnswer = userAnswer.toLowerCase().trim();
    const correctAnswer = currentShape.name.toLowerCase();
    const isCorrect = normalizedAnswer === correctAnswer;

    setAttempts(prev => prev + 1);

    if (isCorrect) {
      setFeedback({ type: 'correct', message: `Bravo ! C'est bien un ${currentShape.name} !` });
      setCompletedShapes(prev => new Set(Array.from(prev).concat(currentShapeIndex)));
      onAction?.('shape_recognized', { 
        shapeId: currentShape.id, 
        isCorrect: true, 
        attempts: attempts + 1 
      });
    } else {
      setFeedback({ type: 'incorrect', message: `Ce n'est pas tout à fait ça. Essayez encore !` });
      onAction?.('shape_recognized', { 
        shapeId: currentShape.id, 
        isCorrect: false, 
        attempts: attempts + 1 
      });
    }
  }, [userAnswer, currentShape, attempts, onAction, currentShapeIndex]);

  const handleNextShape = useCallback(() => {
    if (isLastShape) {
      // All shapes completed
      onAction?.('all_shapes_completed', { 
        totalShapes: resource.shapes.length,
        completedShapes: completedShapes.size 
      });
      return;
    }

    setCurrentShapeIndex(prev => prev + 1);
    setShowAnswer(false);
    setUserAnswer('');
    setFeedback(null);
    setAttempts(0);
  }, [isLastShape, resource.shapes.length, completedShapes.size, onAction]);

  const handleShowAnswer = useCallback(() => {
    setShowAnswer(true);
    setFeedback({ type: 'hint', message: `C'est un ${currentShape.name} !` });
    onAction?.('hint_used', { shapeId: currentShape.id });
  }, [currentShape, onAction]);

  const handleReset = useCallback(() => {
    setCurrentShapeIndex(0);
    setShowAnswer(false);
    setUserAnswer('');
    setFeedback(null);
    setCompletedShapes(new Set());
    setAttempts(0);
    onAction?.('component_reset', {});
  }, [onAction]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAnswerSubmit();
    }
  }, [handleAnswerSubmit]);

  const getProgressPercentage = () => {
    return Math.round((completedShapes.size / resource.shapes.length) * 100);
  };

  const getDifficultyColor = () => {
    switch (resource.difficultyLevel) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-800">{resource.name}</h3>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor()}`}>
              {resource.difficultyLevel === 'easy' ? 'Facile' : 
               resource.difficultyLevel === 'medium' ? 'Moyen' : 'Difficile'}
            </span>
            <span className="text-sm text-gray-500">
              {currentShapeIndex + 1} / {resource.shapes.length}
            </span>
          </div>
        </div>
        
        <p className="text-gray-600 mb-4">{resource.instructions}</p>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 text-center">
          Progression : {completedShapes.size} / {resource.shapes.length} formes reconnues
        </p>
      </div>

      {/* Current Shape */}
      <div className="mb-6">
        <div className="text-center mb-6">
          <div className="text-8xl mb-4">{currentShape.emoji}</div>
          {showAnswer && (
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {currentShape.name}
            </div>
          )}
        </div>

        {/* Shape Properties */}
        {showAnswer && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-800 mb-2">Propriétés :</h4>
            <ul className="text-blue-700 space-y-1">
              {currentShape.properties.map((property, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2 text-blue-500" />
                  {property}
                </li>
              ))}
            </ul>
            <p className="text-blue-600 mt-2 italic">{currentShape.description}</p>
          </div>
        )}

        {/* Answer Input */}
        {!isCompleted && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment s'appelle cette forme ?
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tapez le nom de la forme..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={showAnswer}
              />
              <button
                onClick={handleAnswerSubmit}
                disabled={!userAnswer.trim() || showAnswer}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Valider
              </button>
            </div>
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div className={`p-4 rounded-lg mb-4 ${
            feedback.type === 'correct' ? 'bg-green-100 border border-green-300' :
            feedback.type === 'incorrect' ? 'bg-red-100 border border-red-300' :
            'bg-yellow-100 border border-yellow-300'
          }`}>
            <div className="flex items-center">
              {feedback.type === 'correct' && <CheckCircle className="w-5 h-5 text-green-600 mr-2" />}
              {feedback.type === 'incorrect' && <AlertCircle className="w-5 h-5 text-red-600 mr-2" />}
              {feedback.type === 'hint' && <Eye className="w-5 h-5 text-yellow-600 mr-2" />}
              <span className={`font-medium ${
                feedback.type === 'correct' ? 'text-green-800' :
                feedback.type === 'incorrect' ? 'text-red-800' :
                'text-yellow-800'
              }`}>
                {feedback.message}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 justify-center">
          {!showAnswer && showHints && (
            <button
              onClick={handleShowAnswer}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Voir la réponse
            </button>
          )}
          
          {isCompleted && !isLastShape && (
            <button
              onClick={handleNextShape}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Forme suivante
            </button>
          )}
          
          {isLastShape && completedShapes.size === resource.shapes.length && (
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-2">🎉 Félicitations !</div>
              <p className="text-gray-600 mb-4">Vous avez reconnu toutes les formes !</p>
              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <RotateCcw className="w-4 h-4" />
                Recommencer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* All Shapes Overview */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-700 mb-3">Toutes les formes :</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {resource.shapes.map((shape, index) => (
            <div
              key={shape.id}
              className={`p-3 rounded-lg border-2 transition-all ${
                index === currentShapeIndex 
                  ? 'border-blue-500 bg-blue-50' 
                  : completedShapes.has(index)
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="text-3xl text-center mb-2">{shape.emoji}</div>
              <div className="text-sm font-medium text-center">
                {completedShapes.has(index) ? shape.name : '?'}
              </div>
              {completedShapes.has(index) && (
                <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-1" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { ShapeRecognitionComponent };
export default ShapeRecognitionComponent;
